/**
 * The canonical form of an absolute https link, or null when the value is not one.
 *
 * Used for Floorfy tour links. Floorfy share links are full URLs, and we do not pin the domain: a Floorfy
 * account can serve its tours from a custom domain. Plain http is refused, as browsers block it inside our
 * https galleries. `URL.href` percent-encodes quotes and spaces, so the stored link stays one attribute
 * value in the embed code.
 */
export function toHttpsUrl(value: string): string | null {
  try {
    const url = new URL(value.trim())
    return url.protocol === 'https:' ? url.href : null
  } catch {
    return null
  }
}

/**
 * Turns what an admin typed in the gallery's virtual visit field into the URL we store.
 *
 * A Matterport tour can be given as its bare model ID, which is expanded into a Matterport show link.
 * A Floorfy tour is never a Matterport model: a link without a scheme gets `https://`, and anything that is
 * still not an https link is refused with null.
 */
export function toVirtualVisitUrl(input: string, isFloorfyTour: boolean): string | null {
  const trimmedInput = input.trim()
  if (isFloorfyTour) return toHttpsUrl(trimmedInput.startsWith('http') ? trimmedInput : `https://${trimmedInput}`)
  if (trimmedInput.startsWith('http')) return trimmedInput
  return `https://my.matterport.com/show/?m=${trimmedInput}`
}

/**
 * Escapes the characters that would end a double-quoted HTML attribute or open markup, such as in the embed code's iframe src.
 * `&` is left as is: clients paste the embed code into listing portals, and Matterport links carry `&brand=0` or `&mls=2`.
 */
export function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}
